import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { HandHeart, Loader2, TriangleAlert, CheckCircle2 } from 'lucide-react';
import { cancelRecurringByToken } from '../lib/donations';

type Phase = 'idle' | 'cancelling' | 'done' | 'error';

/**
 * Spec 007c — public "manage your recurring donation" page. Reached from the single-purpose cancel link in
 * the confirmation email (no login). We deliberately do NOT auto-cancel on load (an email prefetch/scanner
 * must not stop someone's donation) and we show NO donation details (the token is cancel-only — no PII, no
 * existence oracle). One action: stop future payments. Four states: idle → cancelling (loading) → done
 * (success) → error (a specific message for an invalid/already-used link). Already-cancelled is a success.
 */
export default function DonateRecurringManage() {
  const { token = '' } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);

  async function onCancel() {
    setError(null);
    setPhase('cancelling');
    const r = await cancelRecurringByToken(token);
    if (r.kind === 'ok') {
      setPhase('done');
      return;
    }
    setPhase('error');
    if (r.kind === 'notfound') {
      setError('This link isn’t valid, or it has already been used. Nothing was changed.');
    } else if (r.kind === 'unavailable') {
      setError(r.message);
    } else {
      setError('We couldn’t stop the payments just now. Please try again in a moment.');
    }
  }

  return (
    <div className="mx-auto w-full max-w-md px-6 py-16">
      <header className="text-center">
        <HandHeart aria-hidden className="accent-glow mx-auto h-9 w-9 text-accent" />
        <h1 className="mt-4 font-display text-2xl font-semibold uppercase tracking-[0.12em] text-ink">
          Manage your donation
        </h1>
        <p className="mx-auto mt-3 max-w-sm font-serif text-base leading-relaxed text-muted">
          Stop your recurring monthly gift whenever you like. Payments already made stay in the open books —
          nothing is refunded or clawed back.
        </p>
      </header>

      {phase === 'done' ? (
        <div
          role="status"
          className="mt-8 flex flex-col items-center rounded-md border border-accent/40 bg-accent/10 px-6 py-10 text-center"
        >
          <CheckCircle2 aria-hidden className="h-8 w-8 text-accent" />
          <p className="mt-3 font-serif text-lg text-ink">Your recurring donation is stopped.</p>
          <p className="mt-1 font-sans text-sm text-muted">
            You won’t be charged again. Thank you for your support.
          </p>
        </div>
      ) : (
        <div className="mt-8 space-y-4">
          {error && (
            <p
              role="alert"
              className="flex items-start gap-2 rounded-md border border-error/40 bg-error/5 px-3 py-2 font-sans text-sm text-error"
            >
              <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
              {error}
            </p>
          )}
          <button
            type="button"
            onClick={onCancel}
            disabled={phase === 'cancelling'}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-accent px-4 py-2.5 font-sans text-sm font-medium text-bg transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
          >
            {phase === 'cancelling' ? (
              <>
                <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
                Stopping future payments…
              </>
            ) : (
              <>Stop future payments</>
            )}
          </button>
          <p className="text-center font-sans text-xs text-muted">
            Changed your mind? You can simply close this page — nothing happens until you choose to stop.
          </p>
        </div>
      )}
    </div>
  );
}
